/**
 * @Author: Anandp
 * @Date:   2024-04-17 18:40:50
 * @Last Modified by:   Eswari Thirumeni
 * @Last Modified Date: 2026-10-15
 */
import ReactDOM from 'react-dom/client';
import { Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { App } from 'src/app';
import mtpDashboardResultsApi from './services/mtpDashboardApi';
import { Buffer } from 'buffer';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { SidebarProvider } from './contexts/SidebarContext'; // Import SidebarProvider
import { JoyrideProvider } from './contexts/JoyrideContext';
import axios from 'axios';
import { Url } from 'src/config';
import { getSessionKey } from './utils/session-storage';
import { toast } from 'react-toastify';

// Configure axios to always send cookies
axios.defaults.withCredentials = true;
axios.defaults.baseURL = Url;
axios.interceptors.request.use(
  (config) => {
    // Ensure credentials (cookies) are sent with requests
    config.withCredentials = true;

    const sessionKey = getSessionKey();
    if (sessionKey) {
      config.headers['X-Session-Key'] = sessionKey;
    }

    const current_login_uuid =
      new URLSearchParams(window.location.search).get('param') ||
      sessionStorage.getItem("login_uuid") ||
      '';
    if (current_login_uuid) {
      config.params = {
        ...config.params,
        login_uuid: current_login_uuid
      };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Cooldown throttle to prevent concurrent API calls from triggering duplicate toasts
let lastRateLimitToastTime = 0;
const RATE_LIMIT_TOAST_COOLDOWN_MS = 3000;

// Eject previous response interceptor if module re-evaluates (e.g. HMR or re-import)
if (window.__axiosResponseInterceptorId !== undefined) {
  axios.interceptors.response.eject(window.__axiosResponseInterceptorId);
}

// Response interceptor - handle authentication failures and rate limiting
window.__axiosResponseInterceptorId = axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle rate limiting (429 Too Many Requests)
    if (error.response?.status === 429) {
      const now = Date.now();
      // Synchronous cooldown check + toast.isActive check prevents duplicate toasts from concurrent requests
      if (
        now - lastRateLimitToastTime > RATE_LIMIT_TOAST_COOLDOWN_MS &&
        !toast.isActive('rate-limit-toast')
      ) {
        lastRateLimitToastTime = now;

        const retryAfter =
          error.response?.data?.data?.retryAfter ||
          error.response?.headers?.['retry-after'] ||
          '';

        toast.error(
          `Too many requests. ${
            retryAfter
              ? `Please wait ${retryAfter} seconds before trying again.`
              : 'Please wait some time before trying again.'
          }`,
          {
            toastId: 'rate-limit-toast', // Prevents duplicate toasts
            autoClose: retryAfter ? Number(retryAfter) * 1000 : 5000,
            position: 'top-right',
            onClose: () => {
              lastRateLimitToastTime = 0;
            }
          }
        );
      }
    }

    if (
      error.response?.status === 403 &&
      error.response?.data?.message === 'Authentication failed'
    ) {
      sessionStorage.clear();
      localStorage.removeItem('issuer');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root'));

export async function saveAs(surveyId, type, fileName, json, currentRef, isGp) {
  const isMobile = window.matchMedia("(max-width: 767px)").matches;

  try {
    if (type === "html") {
      const downloadButton = document.getElementById('downloadButton');
      downloadButton && (downloadButton.style.display = 'none');
      const downloadButton1 = document.getElementById('downloadButton1');
      downloadButton1 && (downloadButton1.style.display = 'none');
      const downloadButton2 = document.getElementById('downloadButton2');
      downloadButton2 && (downloadButton2.style.display = 'none');
      const downloadButton3 = document.getElementById('downloadButton3');
      downloadButton3 && (downloadButton3.style.display = 'none');
      const downloadButton4 = document.getElementById('downloadButton4');
      downloadButton4 && (downloadButton4.style.display = 'none');
      const downloadButton5 = document.getElementById('downloadButton5');
      downloadButton5 && (downloadButton5.style.display = 'none');
      const hideCard = document.getElementById('hideCard');
      hideCard && (hideCard.style.display = 'none');

      const editGrid = document.getElementById('editGrid');
      let editButton = '';
      if (editGrid) {
        editButton = document.querySelectorAll(`#editGrid .editButton`);
        editButton.forEach(btn => {
          btn.style.display = 'none';
        });
      }

      const drawerElement = document.getElementById('drawerr');
      drawerElement && (drawerElement.style.display = 'none');
      const layoutContainer = document.getElementById('layoutContainer');
      layoutContainer && (layoutContainer.style.paddingLeft = 0);
      const topNavWidth = document.getElementById('topNavWidth');
      topNavWidth && (topNavWidth.style.left = 0);
      topNavWidth && (topNavWidth.style.width = '100%');

      const element = currentRef ? currentRef : document.getElementById('root');
      let htmlContent = element.outerHTML;
      const stylesheets = document.styleSheets;
      for (let i = 0; i < stylesheets.length; i++) {
        const stylesheet = stylesheets[i];
        if (stylesheet.href) {
          fetch(stylesheet.href)
            .then(response => response.text())
            // eslint-disable-next-line
            .then(css => {
              htmlContent += `<style>${css}</style>`;
            });
        } else {
          const rules = stylesheet.cssRules;
          if (rules) {
            let css = '';
            for (let j = 0; j < rules.length; j++) {
              css += rules[j].cssText;
            }
            htmlContent += `<style>${css}</style>`;
          }
        }
      }

      const base64Encoded = Buffer.from(htmlContent).toString('base64');
      const staticJson = {
        "html": base64Encoded
      };
      const response = await mtpDashboardResultsApi.postInviteShareLInk(
        staticJson,
        surveyId
      );

      drawerElement && (drawerElement.style.display = 'block');
      layoutContainer && (layoutContainer.style.paddingLeft = isMobile ? "0px" : "250px");
      topNavWidth && (topNavWidth.style.left = "250px");
      topNavWidth && (topNavWidth.style.width = '');

      downloadButton && (downloadButton.style.display = 'flex');
      downloadButton1 && (downloadButton1.style.display = 'flex');
      downloadButton2 && (downloadButton2.style.display = 'flex');
      downloadButton3 && (downloadButton3.style.display = 'flex');
      downloadButton4 && (downloadButton4.style.display = 'block');
      downloadButton5 && (downloadButton5.style.display = 'flex');
      hideCard && (hideCard.style.display = 'block');

      if (editGrid) {
        editButton.forEach(btn => {
          btn.style.display = 'flex';
        });
      }
      return response;

    } else if (type === "pdf") {
      const downloadButton = document.getElementById('downloadButton');
      downloadButton && (downloadButton.style.display = 'none');
      const downloadButton1 = document.getElementById('downloadButton1');
      downloadButton1 && (downloadButton1.style.display = 'none');
      const downloadButton2 = document.getElementById('downloadButton2');
      downloadButton2 && (downloadButton2.style.display = 'none');
      const downloadButton3 = document.getElementById('downloadButton3');
      downloadButton3 && (downloadButton3.style.display = 'none');
      const downloadButton4 = document.getElementById('downloadButton4');
      downloadButton4 && (downloadButton4.style.display = 'none');
      const downloadButton5 = document.getElementById('downloadButton5');
      downloadButton5 && (downloadButton5.style.display = 'none');
      const hideCard = document.getElementById('hideCard');
      hideCard && (hideCard.style.display = 'none');

      const heatText = document.querySelectorAll('#heatText');
      let scoreTexts = '';
      if (heatText) {
        scoreTexts = document.querySelectorAll(`#heatText .scoreText`);
        heatText.forEach(scoreText => {
          scoreText.style.opacity = 1;
        });
        scoreTexts.forEach(scoreText => {
          scoreText.style.visibility = 'visible';
        });
      }

      const editGrid = document.getElementById('editGrid');
      let editButton = '';
      if (editGrid) {
        editButton = document.querySelectorAll(`#editGrid .editButton`);
        editButton.forEach(btn => {
          btn.style.display = 'none';
        });
      }

      const layoutContainer = document.getElementById('layoutContainer');
      layoutContainer && (layoutContainer.style.paddingLeft = 0);
      const topNavWidth = document.getElementById('topNavWidth');
      topNavWidth && (topNavWidth.style.left = 0);
      topNavWidth && (topNavWidth.style.width = '100%');
      const drawerElement = document.getElementById('drawerr');
      drawerElement && (drawerElement.style.display = 'none');

      const waitForImagesToLoad = (element) => {
        const images = element.querySelectorAll('img');
        const promises = [];
        images.forEach((img) => {
          if (!img.complete) {
            const imgPromise = new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            });
            promises.push(imgPromise);
          }
        });
        return Promise.all(promises);
      };

      const captureAndGeneratePDF = () => {
        const rootElement = currentRef ? currentRef : document.getElementById('root');
        const style = document.createElement('style');
        style.id = 'remove-shadow-style';
        style.innerHTML = `
          * {
            box-shadow: none !important;
            filter: none !important;
          }
        `;
        document.head.appendChild(style);

        if (window.location.pathname.includes('/prepSheets')) {
          waitForImagesToLoad(rootElement).then(() => {
            const elements = document.querySelectorAll(".MuiAccordion-root ");
            elements.forEach(el => el.classList.add("downloadacc"));

            const downloadPrint = document.querySelectorAll(".downloadPrint");
            downloadPrint.forEach(el => el.classList.add('cc-grid'));

            const PaperShadowPrint = document.querySelectorAll(".PaperShadowPrint");
            PaperShadowPrint.forEach(el => el.classList.add('cc-paper'));

            const elementss = document.querySelectorAll(".MuiCardHeader-action");
            elementss.forEach(el => el.classList.add('teamDownload'));

            const elementsd = document.querySelectorAll(".downloadContent");
            elementsd.forEach(el => el.classList.add("downloaded"));

            const elementsdh = document.querySelectorAll(".dhide");
            elementsdh.forEach(el => el.classList.add("downloaded"));

            const topNavDiv = document.getElementById('topNavWidth');
            topNavDiv.classList.add('hidtopnam');

            window.print();

            topNavWidth && (topNavWidth.style.left = "250px");
            topNavWidth && (topNavWidth.style.width = '');
            drawerElement && (drawerElement.style.display = 'block');
            layoutContainer && (layoutContainer.style.paddingLeft = isMobile ? "0px" : "250px");
            downloadButton && (downloadButton.style.display = 'flex');
            downloadButton1 && (downloadButton1.style.display = 'flex');
            downloadButton2 && (downloadButton2.style.display = 'flex');
            downloadButton3 && (downloadButton3.style.display = 'flex');
            downloadButton4 && (downloadButton4.style.display = 'block');
            downloadButton5 && (downloadButton5.style.display = 'flex');
            hideCard && (hideCard.style.display = 'block');

            const element1 = document.querySelectorAll(".MuiAccordion-root ");
            element1.forEach(el => el.classList.remove("downloadacc"));

            const elements2 = document.querySelectorAll(".MuiCardHeader-action");
            elements2.forEach(el => el.classList.remove('teamDownload'));

            const elementsdd = document.querySelectorAll(".downloadContent");
            elementsdd.forEach(el => el.classList.remove("downloaded"));

            const elementsdh1 = document.querySelectorAll(".dhide");
            elementsdh1.forEach(el => el.classList.remove("downloaded"));

            const downloadPrints = document.querySelectorAll(".downloadPrint");
            downloadPrints.forEach(el => el.classList.remove('cc-grid'));

            const PaperShadowPrints = document.querySelectorAll(".PaperShadowPrint");
            PaperShadowPrints.forEach(el => el.classList.remove('cc-paper'));

            const topNavDiv1 = document.getElementById('topNavWidth');
            topNavDiv1.classList.remove('hidtopnam');

            if (heatText.length > 0) {
              scoreTexts.forEach(scoreText => {
                scoreText.style.visibility = 'hidden';
                heatText.forEach(scoreTextss => {
                  scoreTextss.addEventListener('mouseover', () => {
                    scoreTextss.style.opacity = 1;
                    scoreTexts.forEach(sText => {
                      sText.style.visibility = 'visible';
                    });
                  });

                  scoreTextss.addEventListener('mouseout', () => {
                    scoreTextss.style.opacity = 0.8;
                    scoreTexts.forEach(sText => {
                      sText.style.visibility = 'hidden';
                    });
                  });
                });
              });
            }

            if (editGrid) {
              editButton.forEach(btn => {
                btn.style.display = 'flex';
              });
            }
          });
        } else {
          waitForImagesToLoad(rootElement).then(() => {
            const elements = document.querySelectorAll(".MuiAccordion-root ");
            elements.forEach(el => el.classList.add("downloadacc"));

            const elementss = document.querySelectorAll(".MuiCardHeader-action");
            elementss.forEach(el => el.classList.add('teamDownload'));

            const elementsd = document.querySelectorAll(".downloadContent");
            elementsd.forEach(el => el.classList.add("downloaded"));

            const elementsdh = document.querySelectorAll(".dhide");
            elementsdh.forEach(el => el.classList.add("downloaded"));

            const contentHeight = rootElement.scrollHeight;
            let scale = 1.5;
            if (contentHeight > 5000) {
              scale = 1;
            } else if (contentHeight > 3000) {
              scale = 1.2;
            }

            html2canvas(rootElement, {
              scale: scale,
              logging: true,
              scrollX: 0,
              scrollY: 0,
              width: document.documentElement.scrollWidth,
              height: document.documentElement.scrollHeight,
              useCORS: true
            }).then((canvas) => {
              const imgData = canvas.toDataURL('image/jpeg', 1);

              const imgWidth = canvas.width;
              const imgHeight = canvas.height;

              // 1. Calculate Dimensions for Double-Lined Copyright Box
              const boxWidth = Math.min(imgWidth * 0.9, 1200);
              const boxX = (imgWidth - boxWidth) / 2;
              const boxPaddingX = Math.max(16, Math.round(boxWidth * 0.025));
              const boxPaddingY = Math.max(14, Math.round(boxWidth * 0.02));

              const titleFontSize = Math.max(14, Math.round(imgWidth * 0.0125));
              const bodyFontSize = Math.max(11, Math.round(imgWidth * 0.0105));
              const lineHeight = bodyFontSize * 1.45;

              const disclaimerText =
                "To protect personal information, psychological safety and SmartLeader intellectual property, neither this report nor its contents may be uploaded to or otherwise used with any AI platform or IT system outside of ClickWith.ai.";

              const maxTextWidth = boxWidth - (boxPaddingX * 2);

              const tempPdf = new jsPDF({ unit: 'px' });
              tempPdf.setFont('helvetica', 'normal');
              tempPdf.setFontSize(bodyFontSize);
              const wrappedDisclaimer = tempPdf.splitTextToSize(disclaimerText, maxTextWidth);

              const boxContentHeight =
                titleFontSize + 8 + (wrappedDisclaimer.length * lineHeight);
              const boxHeight = boxContentHeight + (boxPaddingY * 2);

              const footerMarginTop = Math.max(25, Math.round(imgWidth * 0.02));
              const footerMarginBottom = Math.max(20, Math.round(imgWidth * 0.018));
              const totalFooterSectionHeight = boxHeight + footerMarginTop + footerMarginBottom;

              // 2. Initialize PDF with expanded canvas height
              const pdf = new jsPDF({
                orientation:
                  imgWidth > (imgHeight + totalFooterSectionHeight)
                    ? 'landscape'
                    : 'portrait',
                unit: 'px',
                format: [imgWidth, imgHeight + totalFooterSectionHeight]
              });

              // 3. Add Screen Capture
              pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);

              // 4. White Section Below Screen Content
              const footerStartY = imgHeight;
              pdf.setFillColor(255, 255, 255);
              pdf.rect(0, footerStartY, imgWidth, totalFooterSectionHeight, 'F');

              // 5. Draw Double-Border Box (#f8fafc fill, #64748b double border)
              const boxY = footerStartY + footerMarginTop;

              pdf.setFillColor(248, 250, 252);
              pdf.roundedRect(boxX, boxY, boxWidth, boxHeight, 6, 6, 'F');

              // Outer Border (2.5px width)
              pdf.setDrawColor(100, 116, 139);
              pdf.setLineWidth(2.5);
              pdf.roundedRect(boxX, boxY, boxWidth, boxHeight, 6, 6, 'S');

              // Inner Border (inset 3px for double-line effect)
              pdf.setLineWidth(1);
              pdf.roundedRect(boxX + 3, boxY + 3, boxWidth - 6, boxHeight - 6, 4, 4, 'S');

              // 6. Centered Title (Bold, #1e293b)
              const centerX = imgWidth / 2;
              let textCursorY = boxY + boxPaddingY + titleFontSize;

              pdf.setFont('helvetica', 'bold');
              pdf.setFontSize(titleFontSize);
              pdf.setTextColor(30, 41, 59);
              pdf.text("Copyright 2026. Smart Leader Ltd.", centerX, textCursorY, {
                align: 'center'
              });

              // 7. Centered Multi-line Disclaimer (Normal, #334155)
              textCursorY += 8;
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(bodyFontSize);
              pdf.setTextColor(51, 65, 85);

              wrappedDisclaimer.forEach((line) => {
                textCursorY += lineHeight;
                pdf.text(line, centerX, textCursorY, { align: 'center' });
              });

              // 8. Save PDF
              pdf.save(fileName ? `${fileName}.pdf` : 'app.pdf');

              const styleElement = document.getElementById('remove-shadow-style');
              styleElement && styleElement.remove();

              topNavWidth && (topNavWidth.style.left = "250px");
              topNavWidth && (topNavWidth.style.width = '');
              drawerElement && (drawerElement.style.display = 'block');
              layoutContainer && (layoutContainer.style.paddingLeft = isMobile ? "0px" : "250px");

              downloadButton && (downloadButton.style.display = 'flex');
              downloadButton1 && (downloadButton1.style.display = 'flex');
              downloadButton2 && (downloadButton2.style.display = 'flex');
              downloadButton3 && (downloadButton3.style.display = 'flex');
              downloadButton4 && (downloadButton4.style.display = 'block');
              downloadButton5 && (downloadButton5.style.display = 'flex');
              hideCard && (hideCard.style.display = 'block');

              const element1 = document.querySelectorAll(".MuiAccordion-root ");
              element1.forEach(el => el.classList.remove("downloadacc"));

              const elements2 = document.querySelectorAll(".MuiCardHeader-action");
              elements2.forEach(el => el.classList.remove('teamDownload'));

              const elementsdd = document.querySelectorAll(".downloadContent");
              elementsdd.forEach(el => el.classList.remove("downloaded"));

              const elementsdh1 = document.querySelectorAll(".dhide");
              elementsdh1.forEach(el => el.classList.remove("downloaded"));

              if (heatText.length > 0) {
                scoreTexts.forEach(scoreText => {
                  scoreText.style.visibility = 'hidden';
                  heatText.forEach(scoreTextss => {
                    scoreTextss.addEventListener('mouseover', () => {
                      scoreTextss.style.opacity = 1;
                      scoreTexts.forEach(sText => {
                        sText.style.visibility = 'visible';
                      });
                    });

                    scoreTextss.addEventListener('mouseout', () => {
                      scoreTextss.style.opacity = 0.8;
                      scoreTexts.forEach(sText => {
                        sText.style.visibility = 'hidden';
                      });
                    });
                  });
                });
              }

              if (editGrid) {
                editButton.forEach(btn => {
                  btn.style.display = 'flex';
                });
              }
            });
          });
        }
      };

      if (isGp) {
        captureAndGeneratePDF();
      } else {
        captureAndGeneratePDF();
      }

      return false;
    } else if (type === "csv") {
      const convertToCsv = () => {
        const replacer = (key, value) => (value === null ? '' : value);
        const header = Object.keys(json[0]);
        let csv = json.map((row) =>
          header
            .map((fieldName) => JSON.stringify(row[fieldName], replacer))
            .join(',')
        );
        csv.unshift(header.join(','));
        csv = csv.join('\r\n');
        return csv;
      };
      const csv = convertToCsv();
      const csvData = new Blob([csv], { type: 'text/csv' });
      const csvUrl = URL.createObjectURL(csvData);
      const hiddenElement = document.createElement('a');
      hiddenElement.href = csvUrl;
      hiddenElement.target = '_blank';
      hiddenElement.download = fileName ? `${fileName}.csv` : 'app.csv';
      hiddenElement.click();
    }
  } catch (error) {
    console.log(error.message);
  }
}

root.render(
  <HelmetProvider>
    <BrowserRouter>
      <JoyrideProvider>
        <SidebarProvider>
          <Suspense>
            <App />
          </Suspense>
        </SidebarProvider>
      </JoyrideProvider>
    </BrowserRouter>
  </HelmetProvider>
);